import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { useTable, RowSelectionState } from '@tanstack/react-table'

import { DataGrid, DataGridColumnGroup } from '../../../../design-system/DataGrid'
import { useMediaQuery } from '../../../../hooks/use-media-query'
import { EmptyState } from '../../../../design-system/EmptyState'
import { IUserFilteredInfo } from '../../../../types/users'
import { usersTableFeatures } from '../columns/tableSetup'
import { COLUMN_IDS } from '../columns/columnIds'

const MIN_GRID_HEIGHT = 400
const EMPTY_SELECTION: RowSelectionState = {}
const noop = () => undefined
const NARROW_VIEWPORT_QUERY = '(max-width: 640px)'
const NARROW_NAME_WIDTH = 200

export interface IUsersTableProps {
  users: IUserFilteredInfo[]
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns: any
  isLoading: boolean
  hasNextPage: boolean
  isFetchingNextPage: boolean
  fetchNextPage: () => void
  columnOrder: string[]
  columnVisibility: Record<string, boolean>
  columnWidths: Record<string, number>
  rowSelection?: RowSelectionState
  onRowSelectionChange?: (selection: RowSelectionState) => void
  canSelectRow?: (user: IUserFilteredInfo) => boolean
  pinnedStart?: string[]
  numericColumnIds?: string[]
  columnGroups?: DataGridColumnGroup[]
  density?: 'comfortable' | 'compact'
  /** `all` shares a wide window's leftover width across every text column in proportion to what
   * each already takes. `name` pools it into the name column, as the grid shipped. */
  flexColumns?: 'name' | 'all'
  wrapColumnIds?: string[]
}

const EMPTY_USERS: IUserFilteredInfo[] = []

export const UsersTable = (props: IUsersTableProps): React.ReactElement => {
  const {
    users,
    columns,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    columnOrder,
    columnVisibility,
    columnWidths,
    rowSelection = EMPTY_SELECTION,
    onRowSelectionChange = noop,
    canSelectRow,
    pinnedStart,
    numericColumnIds,
    columnGroups,
    density,
    flexColumns = 'name',
    wrapColumnIds,
  } = props
  const { formatMessage } = useIntl()

  const containerRef = useRef<HTMLDivElement | null>(null)
  const sentinelRef = useRef<HTMLDivElement>(null)
  const fetchNextPageRef = useRef(fetchNextPage)
  fetchNextPageRef.current = fetchNextPage
  const shouldFetchRef = useRef(false)
  shouldFetchRef.current = hasNextPage && !isFetchingNextPage && !isLoading

  const [maxHeight, setMaxHeight] = useState(MIN_GRID_HEIGHT)
  const isNarrowViewport = useMediaQuery(NARROW_VIEWPORT_QUERY)
  const effectiveColumnWidths = useMemo(() => (isNarrowViewport ? { ...columnWidths, [COLUMN_IDS.name]: NARROW_NAME_WIDTH } : columnWidths), [columnWidths, isNarrowViewport])

  const table = useTable({
    features: usersTableFeatures,
    columns,
    data: users.length ? users : EMPTY_USERS,
    getRowId: (row) => row.id,
    enableRowSelection: canSelectRow ? (row) => canSelectRow(row.original) : false,
    state: {
      columnOrder,
      columnVisibility,
      rowSelection,
    },
    onRowSelectionChange: (updater) => {
      const next = typeof updater === 'function' ? updater(rowSelection) : updater
      onRowSelectionChange(next)
    },
  })

  useLayoutEffect(() => {
    const measure = () => {
      const container = containerRef.current
      if (!container) {
        return
      }
      const top = container.getBoundingClientRect().top
      setMaxHeight(Math.max(MIN_GRID_HEIGHT, Math.floor(window.innerHeight - top - 72)))
    }
    measure()
    window.addEventListener('resize', measure)
    return () => window.removeEventListener('resize', measure)
  }, [])

  useEffect(() => {
    const sentinel = sentinelRef.current
    const container = containerRef.current
    if (!sentinel) {
      return
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting) && shouldFetchRef.current) {
          fetchNextPageRef.current()
        }
      },
      { root: container, rootMargin: '400px' }
    )
    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [users.length, maxHeight])

  const showEmpty = !isLoading && users.length === 0
  const flexColumnIds = useMemo(
    () => (flexColumns === 'all' ? Object.keys(effectiveColumnWidths).filter((id) => id !== COLUMN_IDS.select && id !== COLUMN_IDS.actions) : [COLUMN_IDS.name]),
    [flexColumns, effectiveColumnWidths]
  )

  return (
    <div className="vt-users-table-wrap">
      <DataGrid<IUserFilteredInfo>
        table={table}
        loading={isLoading}
        columnWidths={effectiveColumnWidths}
        stickyLeftColumnIds={pinnedStart ?? [COLUMN_IDS.select, COLUMN_IDS.name]}
        stickyRightColumnIds={[COLUMN_IDS.actions]}
        flexColumnIds={flexColumnIds}
        chromeColumnIds={[COLUMN_IDS.select, COLUMN_IDS.actions]}
        rowActionsColumnId={COLUMN_IDS.actions}
        numericColumnIds={numericColumnIds}
        wrapColumnIds={wrapColumnIds}
        columnGroups={columnGroups}
        density={density}
        separators="uniform"
        trailingSkeletonRows={isFetchingNextPage ? 3 : 0}
        maxHeight={maxHeight}
        containerRef={containerRef}
        footer={<div ref={sentinelRef} aria-hidden className="vt-users-table-sentinel" />}
      />
      {showEmpty && <EmptyState description={formatMessage({ id: 'users.table.noResults' })} />}
    </div>
  )
}
